"use client";

import { useAppSelector } from "@/data/redux/store";
import { isHalloweenDate } from "@/styles/holiday";
import { useEffect, useRef, useState } from "react";

// Bats: port of the jquery-halloween-bats plugin (Artimon) — same physics and sprite,
// one shared interval pair instead of two per bat. Sprite: 4 frames of 35x20.
const AMOUNT = 8;
const WIDTH = 35;
const HEIGHT = (WIDTH / 7) * 4;
const FRAMES = 4;
const SPEED = 4; // higher = faster
const FLICKERING = 4; // higher = smoother/slower steering

type BatState = { x: number; y: number; tx: number; ty: number; dx: number; dy: number; frame: number };

// Quarter cobweb for a corner, mirrored per corner in CSS. Deliberately imperfect:
// uneven spoke angles (3/34/61/89°), cross-threads sag outward like drooping silk,
// and the outer ring is torn — only two of its three segments survive.
function Cobweb({ corner }: { corner: "tl" | "tr" | "bl" | "br" }) {
  return (
    <svg className={`halloween-cobweb halloween-cobweb--${corner}`} viewBox="0 0 120 120">
      <g fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round">
        <path d="M0 0L119.8 6.3 M0 0L99.5 67.1 M0 0L58.2 105 M0 0L2.1 120" />
        <path d="M42 2.2Q44 18 34.8 23.5Q32 37 20.4 36.7Q12 46 0.7 42" />
        <path d="M71.9 3.8Q73 27 59.7 40.3Q53 60 34.9 63Q20 75 1.3 72" />
        <path d="M101.9 5.3Q103 36 84.5 57Q76 83 49.5 89.2" />
      </g>
    </svg>
  );
}

export default function HalloweenEffects() {
  const containerRef = useRef<HTMLDivElement>(null);
  const holidayMode = useAppSelector((state) => state.app.holidayMode);
  // Mount gate: the server renders with holidayMode=true (store SSR default), so skip first paint to avoid a hydration mismatch when disabled
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted || !holidayMode || !isHalloweenDate(new Date())) return;
    // scoped to .halloween-bat — the cobweb SVGs are children too and must not fly
    const els = (containerRef.current?.querySelectorAll(".halloween-bat") ??
      []) as unknown as HTMLSpanElement[];
    if (els.length === 0) return;

    const randPos = (screen: number, image: number) => Math.random() * (screen - image);

    const bats: BatState[] = Array.from(els).map(() => ({
      x: randPos(window.innerWidth, WIDTH),
      y: randPos(window.innerHeight, HEIGHT),
      tx: randPos(window.innerWidth, WIDTH),
      ty: randPos(window.innerHeight, HEIGHT),
      dx: -5 + Math.random() * 10,
      dy: -5 + Math.random() * 10,
      frame: Math.round(Math.random() * FRAMES)
    }));

    const move = () => {
      for (let i = 0; i < bats.length; ++i) {
        const b = bats[i];
        const left = b.tx - b.x;
        const top = b.ty - b.y;
        const length = Math.max(1, Math.sqrt(left * left + top * top));

        const dLeft = SPEED * (left / length);
        const dTop = SPEED * (top / length);
        b.dx += (dLeft - b.dx) / FLICKERING;
        b.dy += (dTop - b.dy) / FLICKERING;
        b.x += b.dx;
        b.y += b.dy;

        // window size read per tick, so no resize listener needed
        b.x = Math.max(0, Math.min(b.x, window.innerWidth - WIDTH));
        b.y = Math.max(0, Math.min(b.y, window.innerHeight - HEIGHT));

        if (Math.random() > 0.95) {
          b.tx = randPos(window.innerWidth, WIDTH);
          b.ty = randPos(window.innerHeight, HEIGHT);
        }

        els[i].style.left = `${b.x}px`;
        els[i].style.top = `${b.y}px`;
      }
    };

    const animate = () => {
      for (let i = 0; i < bats.length; ++i) {
        const b = bats[i];
        b.frame += 1;
        if (b.frame >= FRAMES) b.frame -= FRAMES;
        els[i].style.backgroundPosition = `0 ${b.frame * -HEIGHT}px`;
      }
    };

    const moveTimer = window.setInterval(move, 40);
    const frameTimer = window.setInterval(animate, 200);
    // reveal only once positioned, so SSR markup (top-left stack) never flashes
    els.forEach((el) => (el.style.opacity = "1"));

    return () => {
      window.clearInterval(moveTimer);
      window.clearInterval(frameTimer);
    };
  }, [mounted, holidayMode]);

  if (!mounted || !holidayMode || !isHalloweenDate(new Date())) return null;

  return (
    <div ref={containerRef} className="halloween-effects" aria-hidden>
      <Cobweb corner="tl" />
      {/* <Cobweb corner="tr" /> */}
      {/* <Cobweb corner="bl" /> */}
      <Cobweb corner="br" />
      {Array.from({ length: AMOUNT }).map((_, i) => (
        <span key={i} className="halloween-bat" style={{ width: WIDTH, height: HEIGHT }} />
      ))}
    </div>
  );
}
